/**
 * @description
 * Component for displaying deployment health status with visual indicators.
 * Shows worker, database, domain, and overall health with detailed information.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date 2025-01-08
 */

import React from 'react';
import { CheckCircle, AlertCircle, XCircle, Clock, Activity } from 'lucide-react';

interface HealthStatusProps {
  health: {
    overall: 'healthy' | 'degraded' | 'unhealthy';
    worker: {
      status: 'healthy' | 'degraded' | 'unhealthy';
      details: string;
      lastDeployed?: string;
      responseTime?: number;
    };
    database: {
      status: 'healthy' | 'degraded' | 'unhealthy';
      details: string;
      connected: boolean;
      responseTime?: number;
    };
    domain: {
      status: 'healthy' | 'degraded' | 'unhealthy';
      details: string;
      resolved: boolean;
      sslValid: boolean;
      responseTime?: number;
    };
    timestamp: string;
  };
  className?: string;
}

export function DeploymentHealthStatus({ health, className = '' }: HealthStatusProps) {
  const getStatusIcon = (status: 'healthy' | 'degraded' | 'unhealthy') => {
    switch (status) {
      case 'healthy':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'degraded':
        return <AlertCircle className="w-4 h-4 text-yellow-500" />;
      case 'unhealthy':
        return <XCircle className="w-4 h-4 text-red-500" />;
    }
  };

  const getStatusColor = (status: 'healthy' | 'degraded' | 'unhealthy') => {
    switch (status) {
      case 'healthy':
        return 'text-green-700 bg-green-50 border-green-200';
      case 'degraded':
        return 'text-yellow-700 bg-yellow-50 border-yellow-200';
      case 'unhealthy':
        return 'text-red-700 bg-red-50 border-red-200';
    }
  };

  const formatResponseTime = (time?: number) => {
    if (!time) return null;
    return `${time}ms`;
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Overall Status */}
      <div className={`p-4 rounded-lg border ${getStatusColor(health.overall)}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {getStatusIcon(health.overall)}
            <h3 className="font-semibold">Overall Health</h3>
          </div>
          <div className="text-sm opacity-75">
            {formatTimestamp(health.timestamp)}
          </div>
        </div>
        <p className="text-sm mt-1 capitalize">
          Status: {health.overall}
        </p>
      </div>

      {/* Component Status */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Worker Status */}
        <div className="p-4 border rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Activity className="w-4 h-4 text-blue-500" />
            <h4 className="font-medium">Worker</h4>
            {getStatusIcon(health.worker.status)}
          </div>
          <p className="text-sm text-gray-600 mb-2">{health.worker.details}</p>
          {health.worker.responseTime && (
            <div className="flex items-center gap-1 text-xs text-gray-500">
              <Clock className="w-3 h-3" />
              {formatResponseTime(health.worker.responseTime)}
            </div>
          )}
          {health.worker.lastDeployed && (
            <div className="text-xs text-gray-500 mt-1">
              Last deployed: {new Date(health.worker.lastDeployed).toLocaleDateString()}
            </div>
          )}
        </div>

        {/* Database Status */}
        <div className="p-4 border rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Activity className="w-4 h-4 text-purple-500" />
            <h4 className="font-medium">Database</h4>
            {getStatusIcon(health.database.status)}
          </div>
          <p className="text-sm text-gray-600 mb-2">{health.database.details}</p>
          <div className="flex items-center gap-2 text-xs">
            <span className={`px-2 py-1 rounded ${health.database.connected ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
              {health.database.connected ? 'Connected' : 'Disconnected'}
            </span>
            {health.database.responseTime && (
              <span className="text-gray-500">
                {formatResponseTime(health.database.responseTime)}
              </span>
            )}
          </div>
        </div>

        {/* Domain Status */}
        <div className="p-4 border rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Activity className="w-4 h-4 text-orange-500" />
            <h4 className="font-medium">Domain</h4>
            {getStatusIcon(health.domain.status)}
          </div>
          <p className="text-sm text-gray-600 mb-2">{health.domain.details}</p>
          <div className="flex items-center gap-2 text-xs">
            <span className={`px-2 py-1 rounded ${health.domain.resolved ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
              {health.domain.resolved ? 'Resolved' : 'Not Resolved'}
            </span>
            <span className={`px-2 py-1 rounded ${health.domain.sslValid ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
              {health.domain.sslValid ? 'SSL Valid' : 'SSL Invalid'}
            </span>
            {health.domain.responseTime && (
              <span className="text-gray-500">
                {formatResponseTime(health.domain.responseTime)}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default DeploymentHealthStatus;
